/**
 * 集成测试套件
 * 测试 Gongfu 各模块之间的协同工作
 */

import { expect } from 'chai';
import * as fs from 'fs-extra';
import * as path from 'path';
import { spawn } from 'child_process';
import * as request from 'supertest';
import { WorkflowManager } from '../../core/workflow-manager';
import { TaskStore } from '../../core/task-store';
import { TemplateManager } from '../../core/template-system';
import { AgentManager } from '../../core/agent-manager';
import { PluginManager } from '../../core/plugin-system';
import { PromptTemplateEngine } from '../../core/prompt-template-engine';
import { WebhookService } from '../../services/webhook-service';
import { SyncManager } from '../../services/sync-manager';
import { logger } from '../../utils/logger';

/**
 * 测试场景接口
 */
interface IntegrationTest {
  name: string;
  description: string;
  setup?: () => Promise<void>;
  test: () => Promise<void>;
  teardown?: () => Promise<void>;
  timeout?: number;
}

/**
 * 集成测试套件
 */
export class IntegrationTestSuite {
  private testDir: string;
  private tests: IntegrationTest[] = [];
  private results: Array<{
    name: string;
    status: 'passed' | 'failed' | 'skipped';
    error?: string;
    duration: number;
  }> = [];
  
  constructor() {
    this.testDir = path.join(process.cwd(), '.integration-test');
  }
  
  /**
   * 初始化测试环境
   */
  async setup(): Promise<void> {
    await fs.ensureDir(this.testDir);
    await fs.emptyDir(this.testDir);
    
    // 注册所有测试
    this.registerTests();
    
    logger.info('Integration test environment initialized');
  }
  
  /**
   * 清理测试环境
   */
  async teardown(): Promise<void> {
    await fs.remove(this.testDir);
  }
  
  /**
   * 注册测试场景
   */
  private registerTests(): void {
    // 1. 端到端工作流测试
    this.tests.push({
      name: 'End-to-End Workflow',
      description: '测试从创建任务到完成的完整流程',
      test: async () => {
        const workflowManager = new WorkflowManager(this.testDir);
        const taskStore = new TaskStore(this.testDir);
        
        await workflowManager.initialize();
        await taskStore.initialize();
        
        // 创建任务
        const task = await taskStore.createTask({
          content: '集成测试任务',
          priority: 'high',
          tags: ['test', 'integration']
        });
        
        expect(task).to.have.property('id');
        expect(task.status).to.equal('todo');
        
        // 开始任务
        await taskStore.updateTask(task.id, { status: 'in_progress' });
        const current = await taskStore.getCurrentTask();
        expect(current?.id).to.equal(task.id);
        
        // 完成任务
        await taskStore.updateTask(task.id, { status: 'done' });
        const completed = await taskStore.getTaskById(task.id);
        expect(completed?.status).to.equal('done');
        expect(completed?.completedAt).to.exist;
      }
    });
    
    // 2. 模板应用集成测试
    this.tests.push({
      name: 'Template Application Integration',
      description: '测试模板系统与任务系统的集成',
      test: async () => {
        const templateManager = new TemplateManager(this.testDir);
        const taskStore = new TaskStore(this.testDir);
        
        await templateManager.initialize();
        await taskStore.initialize();
        
        // 创建测试模板
        const templateId = await templateManager.createTemplate({
          id: 'integration-test',
          name: '集成测试模板',
          description: '用于集成测试',
          version: '1.0.0',
          category: 'testing'
        }, {
          name: '测试工作流',
          description: '测试描述',
          phases: [{
            id: 'test-phase',
            name: '测试阶段',
            description: '阶段描述',
            tasks: [{
              id: 'test-task',
              content: '测试任务 {{projectName}}',
              priority: 'medium',
              tags: ['test']
            }]
          }]
        });
        
        // 应用模板
        const result = await templateManager.applyTemplate(templateId, {
          projectName: 'test-project',
          projectPath: path.join(this.testDir, 'test-project'),
          variables: {}
        });
        
        expect(result.tasks).to.have.length.greaterThan(0);
        expect(result.errors).to.have.length(0);
        
        // 检查任务是否创建
        const tasks = await taskStore.getAllTasks();
        expect(tasks).to.have.length.greaterThan(0);
      }
    });
    
    // 3. Agent 与任务集成测试
    this.tests.push({
      name: 'Agent Task Integration',
      description: '测试 Agent 系统与任务系统的集成',
      test: async () => {
        const agentManager = new AgentManager(this.testDir);
        const taskStore = new TaskStore(this.testDir);
        
        await agentManager.initialize();
        await taskStore.initialize();
        
        // 注册测试 Agent
        await agentManager.registerAgent({
          id: 'test-agent',
          name: '测试 Agent',
          description: '集成测试用',
          capabilities: ['coding', 'testing'],
          systemPrompt: 'You are a test agent',
          examples: []
        });
        
        // 创建任务
        const task = await taskStore.createTask({
          content: '编写测试代码',
          type: 'coding',
          priority: 'high'
        });
        
        // Agent 选择
        const selectedAgent = await agentManager.selectAgent({
          content: task.content,
          type: task.type
        });
        
        expect(selectedAgent).to.exist;
        expect(selectedAgent?.id).to.equal('test-agent');
        
        // 执行任务
        const result = await agentManager.executeTask(task, selectedAgent!.id);
        expect(result).to.have.property('success');
      }
    });
    
    // 4. 插件系统集成测试
    this.tests.push({
      name: 'Plugin System Integration',
      description: '测试插件系统与核心功能的集成',
      test: async () => {
        const pluginManager = new PluginManager(this.testDir);
        const taskStore = new TaskStore(this.testDir);
        
        await pluginManager.initialize();
        await taskStore.initialize();
        
        // 创建测试插件
        const pluginDir = path.join(this.testDir, 'plugins', 'test-plugin');
        await fs.ensureDir(pluginDir);
        
        await fs.writeJson(path.join(pluginDir, 'plugin.json'), {
          id: 'integration-test-plugin',
          name: '集成测试插件',
          version: '1.0.0',
          description: '测试插件集成',
          main: './index.js',
          permissions: ['tasks:read', 'tasks:write']
        });
        
        // 插件代码
        await fs.writeFile(path.join(pluginDir, 'index.js'), `
          let taskCount = 0;
          
          module.exports = {
            activate: async (api) => {
              // 监听任务创建
              api.hooks.on('beforeTaskCreate', async (task) => {
                taskCount++;
                task.metadata = task.metadata || {};
                task.metadata.pluginProcessed = true;
                task.metadata.taskNumber = taskCount;
                return task;
              });
            },
            
            deactivate: async () => {
              taskCount = 0;
            }
          };
        `);
        
        // 加载并启用插件
        await pluginManager.loadPlugins();
        await pluginManager.enablePlugin('integration-test-plugin');
        
        // 创建任务，验证插件工作
        const task = await taskStore.createTask({
          content: '测试插件处理的任务',
          priority: 'medium'
        });
        
        expect(task.metadata?.pluginProcessed).to.be.true;
        expect(task.metadata?.taskNumber).to.equal(1);
      }
    });
    
    // 5. Prompt 模板集成测试
    this.tests.push({
      name: 'Prompt Template Integration',
      description: '测试 Prompt 模板与任务系统的集成',
      test: async () => {
        const promptEngine = new PromptTemplateEngine(this.testDir);
        const taskStore = new TaskStore(this.testDir);
        
        await promptEngine.initialize();
        await taskStore.initialize();
        
        // 注册测试模板
        await promptEngine.registerTemplate({
          id: 'task-analysis',
          name: '任务分析',
          description: '分析任务复杂度',
          category: 'analysis',
          template: '分析任务: {{task.content}}\n优先级: {{task.priority}}\n标签: {{task.tags}}',
          variables: ['task'],
          examples: []
        });
        
        // 创建任务
        const task = await taskStore.createTask({
          content: '实现用户认证功能',
          priority: 'high',
          tags: ['auth', 'security']
        });
        
        // 渲染模板
        const prompt = await promptEngine.render('task-analysis', { task });
        
        expect(prompt).to.include('实现用户认证功能');
        expect(prompt).to.include('high');
        expect(prompt).to.include('auth');
      }
    });
    
    // 6. 同步服务集成测试
    this.tests.push({
      name: 'Sync Service Integration',
      description: '测试同步服务与各模块的集成',
      test: async () => {
        const syncManager = new SyncManager();
        const taskStore = new TaskStore(this.testDir);
        
        await taskStore.initialize();
        
        // 模拟 webhook 配置
        process.env.WORKFLOW_WEBHOOK_URL = 'http://localhost:3000/webhook';
        process.env.WORKFLOW_WEBHOOK_ENABLED = 'true';
        
        let webhookCalled = false;
        
        // 监听同步事件
        syncManager.on('sync:success', () => {
          webhookCalled = true;
        });
        
        // 创建任务触发同步
        const task = await taskStore.createTask({
          content: '测试同步的任务',
          priority: 'medium'
        });
        
        // 等待异步操作
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // 注意：实际测试中可能需要 mock HTTP 请求
        // expect(webhookCalled).to.be.true;
      }
    });
    
    // 7. CLI 命令集成测试
    this.tests.push({
      name: 'CLI Commands Integration',
      description: '测试 CLI 命令的端到端执行',
      test: async () => {
        // 测试 workflow init
        await this.runCommand('workflow', ['init', '--path', this.testDir]);
        expect(await fs.pathExists(path.join(this.testDir, '.workflow'))).to.be.true;
        
        // 测试任务创建
        await this.runCommand('workflow', ['task', 'create', 'CLI集成测试任务']);
        
        // 测试任务列表
        const output = await this.runCommand('workflow', ['task', 'list', '--json']);
        const tasks = JSON.parse(output);
        expect(tasks).to.be.an('array');
        expect(tasks).to.have.length.greaterThan(0);
      }
    });
    
    // 8. API 服务集成测试
    this.tests.push({
      name: 'API Service Integration',
      description: '测试 API 服务与各模块的集成',
      test: async () => {
        // 启动 API 服务器
        const apiProcess = spawn('node', [
          path.join(__dirname, '../../api/server.js'),
          '--port', '3456',
          '--path', this.testDir
        ]);
        
        // 等待服务器启动
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        try {
          // 测试 API 端点
          const baseUrl = 'http://localhost:3456';
          
          // 创建任务
          const createRes = await fetch(`${baseUrl}/api/tasks`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              content: 'API 测试任务',
              priority: 'high'
            })
          });
          
          expect(createRes.status).to.equal(201);
          const createdTask = await createRes.json();
          expect(createdTask).to.have.property('id');
          
          // 获取任务列表
          const listRes = await fetch(`${baseUrl}/api/tasks`);
          expect(listRes.status).to.equal(200);
          const tasks = await listRes.json();
          expect(tasks).to.be.an('array');
          
        } finally {
          // 关闭服务器
          apiProcess.kill();
        }
      },
      timeout: 10000
    });
    
    // 9. 文件监视集成测试
    this.tests.push({
      name: 'File Watcher Integration',
      description: '测试文件监视与自动同步',
      test: async () => {
        const taskStore = new TaskStore(this.testDir);
        await taskStore.initialize();
        
        let changeDetected = false;
        
        // 启动文件监视
        const watcher = taskStore.startWatcher();
        watcher.on('change', () => {
          changeDetected = true;
        });
        
        // 直接修改文件
        const tasksPath = path.join(this.testDir, '.workflow/tasks.json');
        const data = await fs.readJson(tasksPath);
        data.tasks.push({
          id: 'watcher-test',
          content: '文件监视测试',
          status: 'todo',
          createdAt: new Date().toISOString()
        });
        await fs.writeJson(tasksPath, data);
        
        // 等待文件变化检测
        await new Promise(resolve => setTimeout(resolve, 500));
        
        expect(changeDetected).to.be.true;
        watcher.close();
      }
    });
    
    // 10. 错误恢复集成测试
    this.tests.push({
      name: 'Error Recovery Integration',
      description: '测试错误处理和恢复机制',
      test: async () => {
        const taskStore = new TaskStore(this.testDir);
        await taskStore.initialize();
        
        // 测试损坏的任务文件恢复
        const tasksPath = path.join(this.testDir, '.workflow/tasks.json');
        await fs.writeFile(tasksPath, '{ invalid json');
        
        // 重新初始化应该能恢复
        await taskStore.initialize();
        const tasks = await taskStore.getAllTasks();
        expect(tasks).to.be.an('array');
        
        // 测试并发写入冲突
        const promises = Array.from({ length: 10 }, (_, i) =>
          taskStore.createTask({
            content: `并发任务 ${i}`,
            priority: 'medium'
          })
        );
        
        const results = await Promise.allSettled(promises);
        const successful = results.filter(r => r.status === 'fulfilled');
        expect(successful.length).to.be.greaterThan(0);
      }
    });
  }
  
  /**
   * 运行所有测试
   */
  async runAll(): Promise<void> {
    logger.info('Starting integration test suite...');
    
    await this.setup();
    
    try {
      for (const test of this.tests) {
        await this.runTest(test);
      }
      
      this.printResults();
      
    } finally {
      await this.teardown();
    }
  }
  
  /**
   * 运行单个测试
   */
  async runTest(test: IntegrationTest): Promise<void> {
    const startTime = Date.now();
    
    logger.info(`\nRunning: ${test.name}`);
    logger.info(`Description: ${test.description}`);
    
    try {
      // Setup
      if (test.setup) {
        await test.setup();
      }
      
      // Run test with timeout
      await Promise.race([
        test.test(),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Test timeout')), test.timeout || 30000)
        )
      ]);
      
      // Teardown
      if (test.teardown) {
        await test.teardown();
      }
      
      const duration = Date.now() - startTime;
      this.results.push({
        name: test.name,
        status: 'passed',
        duration
      });
      
      logger.info(`✅ ${test.name} passed (${duration}ms)`);
      
    } catch (error: any) {
      const duration = Date.now() - startTime;
      this.results.push({
        name: test.name,
        status: 'failed',
        error: error.message,
        duration
      });
      
      logger.error(`❌ ${test.name} failed: ${error.message}`);
    }
  }
  
  /**
   * 运行命令
   */
  private runCommand(command: string, args: string[]): Promise<string> {
    return new Promise((resolve, reject) => {
      const proc = spawn(command, args, {
        cwd: this.testDir,
        env: { ...process.env, NODE_ENV: 'test' }
      });
      
      let output = '';
      let error = '';
      
      proc.stdout.on('data', (data) => {
        output += data.toString();
      });
      
      proc.stderr.on('data', (data) => {
        error += data.toString();
      });
      
      proc.on('close', (code) => {
        if (code === 0) {
          resolve(output);
        } else {
          reject(new Error(error || `Command failed with code ${code}`));
        }
      });
    });
  }
  
  /**
   * 打印测试结果
   */
  private printResults(): void {
    console.log('\n' + '='.repeat(60));
    console.log('INTEGRATION TEST RESULTS');
    console.log('='.repeat(60));
    
    const passed = this.results.filter(r => r.status === 'passed').length;
    const failed = this.results.filter(r => r.status === 'failed').length;
    const total = this.results.length;
    
    console.log(`\nTotal: ${total} | Passed: ${passed} | Failed: ${failed}`);
    console.log('\nDetails:');
    
    this.results.forEach(result => {
      const icon = result.status === 'passed' ? '✅' : '❌';
      console.log(`${icon} ${result.name} (${result.duration}ms)`);
      if (result.error) {
        console.log(`   Error: ${result.error}`);
      }
    });
    
    console.log('\n' + '='.repeat(60));
    
    // 如果有失败的测试，退出码为 1
    if (failed > 0) {
      process.exit(1);
    }
  }
}

/**
 * 运行集成测试
 */
export async function runIntegrationTests(): Promise<void> {
  const suite = new IntegrationTestSuite();
  await suite.runAll();
}